import React from 'react';
import CardLayout from '../../../../../shared/components/card/CardLayout';
import { Button, Col, Row } from 'react-bootstrap';
import { CustomImageHandler } from '../../../../../shared/components/CustomImageHandler';
import { DiscoveryUser, MoodashActivePlatforms } from '../dto/IDiscoveryFilter';
import { FaCopy } from 'react-icons/fa';
import { copyToClipboard } from '../../../../../shared/functions/Functions';
import { numberFormatter } from '../../../../../utils/helpers/numberFormater';
import { HeartFillIcon } from '../../components/CampaignHeader';
import { useTranslation } from 'react-i18next';
import { AiOutlineEye } from 'react-icons/ai';
import { priceSplitter } from '../../../../../utils/helpers/priceSplitter';
import TooltipOverlay from '../../../../../shared/components/tooltip/Tooltip';

interface IDiscoveryInfluencerCardItemProps {
    user: DiscoveryUser;
    selectedPlatform: MoodashActivePlatforms;
    addToFavorites: (ivId: number, externalId: number, isFavorite: boolean) => void;
    openModal: () => void;
    isClient: boolean;
    border?: boolean;
}

const DiscoveryInfluencerCardItem = ({
    user,
    selectedPlatform,
    addToFavorites,
    openModal,
    isClient,
    border = false,
}: IDiscoveryInfluencerCardItemProps) => {
    const { t } = useTranslation();
    const {
        isFavorite,
        ivId,
        externalId,
        fullname,
        username,
        picture,
        engagementRate,
        engagements,
        followers,
        url,
        averageViews,
    } = user.profile;
    const formatedER = priceSplitter(+(engagementRate * 100).toFixed(2));
    return (
        <CardLayout classCard={`mb-2 ${border ? 'border-primary' : ''}`} classBody="p-2">
            {/* Desktop layout */}
            <Row key={user?.userId} className="align-items-center d-none d-md-flex">
                <Col md={2} className="d-flex align-items-center">
                    <CustomImageHandler
                        altTag={fullname}
                        name={fullname ?? username}
                        classes="user-img-rounded me-3"
                        thumbnailPath={picture ?? ''}
                        photoPath={picture ?? ''}
                        style={{ width: '50px', height: '50px', borderRadius: '50%' }}
                    />
                    <div className="ml-2">
                        <strong>{fullname ?? username}</strong>
                        <br />
                        <a target="_blank" href={url} className="text-muted cursor-pointer" rel="noopener noreferrer">
                            @{username}
                        </a>
                        <span>
                            <FaCopy
                                className="pl-1 cursor-pointer textHover"
                                onClick={() => copyToClipboard('@' + username)}
                                size={14}
                            />
                        </span>
                    </div>
                </Col>
                <Col md={2}>
                    <div
                        onClick={() => addToFavorites(ivId, externalId, isFavorite)}
                        className={`icon-container p-2 bg-white ${isFavorite ? 'active' : ''}`}
                        id={`${isClient && isFavorite && 'cursor-initial'}`}
                        aria-label="Add to favorite"
                    >
                        <div aria-label="Add to favorite">
                            {isFavorite ? <HeartFillIcon /> : <i className={`fi fi-rr-heart d-flex font-18`}></i>}
                        </div>
                    </div>
                </Col>
                <Col md={2}>{numberFormatter(followers)}</Col>
                <Col md={2}>{formatedER}%</Col>
                <Col md={2}>
                    {selectedPlatform === MoodashActivePlatforms.INSTAGRAM
                        ? numberFormatter(engagements)
                        : numberFormatter(averageViews)}
                </Col>
                <Col md={2} className="d-flex justify-content-end align-items-center">
                    {/*<Button onClick={openModal} variant="outline-primary">*/}
                    {/*    {user?.hasOffer && !isClient ? t('brand.campaign.discovery.offer')*/}
                    {/*      : (!user?.hasOffer && ivId && !isClient) ? t('brand.campaign.discovery.invite')*/}
                    {/*        : t('brand.campaign.discovery.profile')}*/}
                    {/*</Button>*/}
                    {user?.alreadyViewed && (
                        <TooltipOverlay
                            className="me-2"
                            content={
                                <span className="font-weight-semi-bold mr-2">
                                    {t('brand.campaign.discovery.already_seen')}
                                </span>
                            }
                        >
                            <AiOutlineEye color="grey" size={20} />
                        </TooltipOverlay>
                    )}
                    <Button onClick={openModal} variant="outline-primary">
                        {t('brand.campaign.discovery.profile')}
                    </Button>
                </Col>
            </Row>

            {/* Mobile layout */}
            <div className="d-block d-md-none">
                <div className="d-flex align-items-center mb-2">
                    <CustomImageHandler
                        altTag={fullname ?? username}
                        classes="user-img-rounded mr-2"
                        thumbnailPath={picture ?? ''}
                        photoPath={picture ?? ''}
                        style={{ width: '50px', height: '50px', borderRadius: '50%' }}
                    />
                    <div>
                        <strong>{fullname ?? username}</strong>
                        <br />
                        <a target="_blank" href={url} className="text-muted cursor-pointer" rel="noopener noreferrer">
                            @{username}
                        </a>
                        <span>
                            <FaCopy
                                className="pl-1 cursor-pointer textHover"
                                onClick={() => copyToClipboard('@' + username)}
                                size={14}
                            />
                        </span>
                    </div>
                    <div className="ml-auto">
                        <div
                            onClick={() => addToFavorites(ivId, externalId, isFavorite)}
                            className={`icon-container p-2 bg-white ${isFavorite ? 'active' : ''}`}
                        >
                            <div>
                                {isFavorite ? <HeartFillIcon /> : <i className={`fi fi-rr-heart d-flex font-18`}></i>}
                            </div>
                        </div>
                    </div>
                </div>
                <div className="d-flex flex-wrap justify-content-between mb-2">
                    <div className="mb-1">
                        <span className="text-muted small">
                            {selectedPlatform === MoodashActivePlatforms.INSTAGRAM
                                ? t('brand.campaign.discovery.followers')
                                : t('brand.campaign.discovery.subscribers')}
                            :
                        </span>
                        <span className="ml-1">{numberFormatter(followers)}</span>
                    </div>
                    <div className="mb-1">
                        <span className="text-muted small">{t('brand.campaign.discovery.engagement_rate')}:</span>
                        <span className="ml-1">{formatedER}%</span>
                    </div>
                    <div className="mb-1">
                        <span className="text-muted small">
                            {selectedPlatform === MoodashActivePlatforms.INSTAGRAM
                                ? t('brand.campaign.discovery.engagements')
                                : t('brand.campaign.discovery.views')}
                            :
                        </span>
                        <span className="ml-1">
                            {selectedPlatform === MoodashActivePlatforms.INSTAGRAM
                                ? numberFormatter(engagements)
                                : numberFormatter(averageViews)}
                        </span>
                    </div>
                </div>
                <div className="d-flex justify-content-end align-items-center">
                    {/*<Button onClick={openModal} variant="outline-primary" size="sm">*/}
                    {/*    {user?.hasOffer && !isClient ? t('brand.campaign.discovery.offer')*/}
                    {/*      : (!user?.hasOffer && ivId && !isClient) ? t('brand.campaign.discovery.invite')*/}
                    {/*        : t('brand.campaign.discovery.profile')}*/}
                    {/*</Button>*/}
                    {user?.alreadyViewed && (
                        <TooltipOverlay
                            className="me-2"
                            content={
                                <span className="font-weight-semi-bold mr-2">
                                    {t('brand.campaign.discovery.already_seen')}
                                </span>
                            }
                        >
                            <AiOutlineEye color="grey" size={20} />
                        </TooltipOverlay>
                    )}

                    <Button onClick={openModal} variant="outline-primary" size="sm">
                        {t('brand.campaign.discovery.profile')}
                    </Button>
                </div>
            </div>
        </CardLayout>
    );
};

export default React.memo(DiscoveryInfluencerCardItem);
